import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nfp-tracker/components";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getInstructorClientRoute,
  getInstructorClientTabFromPath,
} from "../constants/routes";
import { useOwnerContext } from "../hooks/useOwnerContext";
import { trackEvent } from "../integrations/analytics/client";
import { listInstructorClients } from "../integrations/supabase/instructor";
import { useInstructorClient } from "./InstructorClientContext";

const SEARCH_DEBOUNCE_MS = 200;
const SWITCHER_PAGE_SIZE = 20;

/**
 * Switch the open client without leaving the page. Closed by default and fetched only while open,
 * so other clients' names are on screen only when the instructor deliberately opens the list —
 * matters when a chart is being screen-shared with the client it belongs to.
 */
export const ClientSwitcher = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeClient } = useInstructorClient();
  const ownerContext = useOwnerContext().data;
  const actorUserId = ownerContext?.status === "ready" ? ownerContext.actorUserId : undefined;

  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearch("");
    }
  }, [open]);

  const clientsQuery = useQuery({
    queryKey: ["instructor-clients", actorUserId, "switcher", search],
    queryFn: () => listInstructorClients({ search, limit: SWITCHER_PAGE_SIZE, offset: 0 }),
    enabled: open && Boolean(actorUserId),
  });

  const clients = clientsQuery.data?.clients ?? [];
  const currentTab = getInstructorClientTabFromPath(pathname) ?? "calendar";

  const handleSelect = (clientUserId: string) => {
    setOpen(false);
    if (clientUserId === activeClient?.clientUserId) return;

    trackEvent("instructor_client_opened", { source: "switcher" });
    navigate(getInstructorClientRoute(clientUserId, currentTab));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Switch client"
          className="h-9 gap-1.5 px-3 text-foreground"
        >
          <span className="hidden sm:inline">Switch client</span>
          <span className="sm:hidden">Switch</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        {/* Search is server-side (name or email), so cmdk's own filtering is switched off. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search clients…"
            value={searchInput}
            onValueChange={setSearchInput}
          />
          <CommandList>
            {clientsQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading clients…
              </div>
            ) : clientsQuery.isError ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Couldn't load clients.
              </div>
            ) : (
              <>
                <CommandEmpty>
                  {search ? "No matching clients." : "No clients yet."}
                </CommandEmpty>
                {clients.map((client) => {
                  const isCurrent = client.clientUserId === activeClient?.clientUserId;

                  return (
                    <CommandItem
                      key={client.clientUserId}
                      value={client.clientUserId}
                      onSelect={() => handleSelect(client.clientUserId)}
                      className="gap-2"
                    >
                      <Check
                        className={`h-4 w-4 shrink-0 ${isCurrent ? "opacity-100" : "opacity-0"}`}
                        aria-hidden="true"
                      />
                      <span className="truncate">{client.displayName}</span>
                      {isCurrent ? <span className="sr-only">(current)</span> : null}
                    </CommandItem>
                  );
                })}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
